import { useId, type FormEvent, type ReactNode } from 'react';
import { Alert } from '@/shared/components/ui/alert/Alert';
import { Button } from '@/shared/components/ui/button/Button';
import { Form } from '@/shared/components/form/Form';
import { Label } from '@/shared/components/form/Label';
import { Modal } from '@/shared/components/ui/modal/Modal';
import { Select } from '@/shared/components/form/Select';
import { ASSIGNABLE_DOCUMENT_TYPES, type AssignableDocumentType } from '../../types/documentTemplates.types';
import { DOCUMENT_TYPE_LABELS } from '../../utils/documentTypeLabels';

export type AssignTemplateMode = 'product-slot' | 'dealer-override' | 'change';

export interface AssignTemplateValues {
  dealerId: string;
  documentType: AssignableDocumentType | '';
  templateId: string;
}

export type AssignTemplateErrors = Partial<Record<keyof AssignTemplateValues, string>>;

interface ContextLine {
  label: string;
  value: ReactNode;
}

interface Props {
  isOpen: boolean;
  mode: AssignTemplateMode;
  values: AssignTemplateValues;
  errors?: AssignTemplateErrors;
  formError?: string;
  contextLines?: ContextLine[];
  templateOptions: { value: string; label: string }[];
  templatesStatus: 'idle' | 'loading' | 'ready';
  templatesNotice?: string;
  dealerPicker?: ReactNode;
  isSaving?: boolean;
  onValuesChange: (patch: Partial<AssignTemplateValues>) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const TITLES: Record<AssignTemplateMode, string> = {
  'product-slot': 'Assign template',
  'dealer-override': 'Add dealer override',
  change: 'Change template',
};

const TEMPLATE_PLACEHOLDERS: Record<Props['templatesStatus'], string> = {
  idle: 'Choose a dealer and document first',
  loading: 'Loading templates...',
  ready: 'Choose a published template',
};

const DOCUMENT_TYPE_SELECT_OPTIONS = ASSIGNABLE_DOCUMENT_TYPES.map((value) => ({ value, label: DOCUMENT_TYPE_LABELS[value] }));

function FieldError({ message }: { message?: string }) {
  return message ? (
    <p role="alert" className="mt-1.5 text-xs text-mp-error">
      {message}
    </p>
  ) : null;
}

export function AssignTemplateModal(props: Props) {
  const { isOpen, mode, values, errors = {}, formError, contextLines = [], templateOptions, templatesStatus, templatesNotice } = props;
  const { dealerPicker, isSaving = false, onValuesChange, onSubmit, onClose } = props;
  const headingId = useId();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} ariaLabelledBy={headingId} className="max-w-lg p-6">
      <h2 id={headingId} className="mb-4 text-lg font-semibold">{TITLES[mode]}</h2>
      <Form className="space-y-4" onSubmit={submit}>
        {formError && <Alert variant="error">{formError}</Alert>}
        {contextLines.length > 0 && (
          <dl className="grid grid-cols-1 gap-2 rounded-lg bg-mp-background p-4 text-sm sm:grid-cols-12">
            {contextLines.map((line) => (
              <div key={line.label} className="contents">
                <dt className="text-mp-muted sm:col-span-4">{line.label}</dt>
                <dd className="font-medium sm:col-span-8">{line.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {mode === 'dealer-override' && (
          <>
            <div>
              <Label htmlFor="assign-dealer" required>Dealer</Label>
              {dealerPicker}
              <FieldError message={errors.dealerId} />
            </div>
            <div>
              <Label id="assign-document-type-label" htmlFor="assign-document-type" required>Document</Label>
              <Select
                id="assign-document-type"
                ariaLabelledBy="assign-document-type-label"
                options={DOCUMENT_TYPE_SELECT_OPTIONS}
                placeholder="Choose a document"
                value={values.documentType}
                error={Boolean(errors.documentType)}
                onChange={(value) => onValuesChange({ documentType: value as AssignableDocumentType, templateId: '' })}
              />
              <FieldError message={errors.documentType} />
            </div>
          </>
        )}
        <div>
          <Label id="assign-template-label" htmlFor="assign-template" required>Template</Label>
          <Select
            id="assign-template"
            ariaLabelledBy="assign-template-label"
            options={templateOptions}
            placeholder={TEMPLATE_PLACEHOLDERS[templatesStatus]}
            value={values.templateId}
            disabled={templatesStatus !== 'ready'}
            error={Boolean(errors.templateId)}
            onChange={(value) => onValuesChange({ templateId: value })}
          />
          <FieldError message={errors.templateId} />
          {templatesStatus === 'ready' && templateOptions.length === 0 && (
            <p className="mt-1.5 text-xs text-mp-muted">
              No published template of this type is available. Publish one first.
            </p>
          )}
          {templatesNotice && <p className="mt-1.5 text-xs text-mp-muted">{templatesNotice}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="neutral" appearance="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
